import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  TrendingUp,
  Target,
  Package,
  CheckCircle2,
  FileText,
  Briefcase,
  Trash2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SendEmailDialog from "@/components/SendEmailDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SCALE_LABELS = ["Inexistente", "Informal", "Parcial", "Estruturado", "Avançado"];

const statColors = (score) => {
  if (score > 80) return "#22B673";
  if (score > 60) return "#6B8DB3";
  if (score > 40) return "#F5A623";
  return "#B9262B";
};

const AdminDiagnosticDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/diagnostics/${id}`);
      toast.success("Diagnóstico excluído.");
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
    }
  };

  useEffect(() => {
    api
      .get(`/diagnostics/${id}/full`)
      .then((r) => {
        setData(r.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#6B8DB3]">
        <Loader2 className="animate-spin mr-2" /> Carregando...
      </div>
    );
  }
  if (!data) return null;

  const { diagnostic: d, sections, questions } = data;
  const scoreMap = Object.fromEntries(
    d.answers
      .filter((a) => a.score !== null && a.score !== undefined)
      .map((a) => [a.question_id, a.score])
  );
  const valueMap = Object.fromEntries(
    d.answers
      .filter((a) => a.value !== null && a.value !== undefined)
      .map((a) => [a.question_id, a.value])
  );
  const total = Math.round(d.scoring?.total || 0);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          className="text-[#9aa3ad] hover:text-white hover:bg-[#1C1F26] -ml-3"
        >
          <ArrowLeft size={16} className="mr-2" /> Voltar
        </Button>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => navigate(`/admin/diagnosticos/${id}/relatorio`)}
            variant="outline"
            data-testid="open-report-btn"
            className="border-[#3A3F47] text-white hover:bg-[#1C1F26]"
          >
            <FileText size={14} className="mr-2" /> Relatório (PDF)
          </Button>
          <Button
            onClick={() => navigate(`/admin/diagnosticos/${id}/proposta`)}
            data-testid="open-proposta-btn"
            variant="outline"
            className="border-[#3A3F47] text-white hover:bg-[#1C1F26]"
          >
            <Briefcase size={14} className="mr-2" /> Proposta Comercial
          </Button>
          <SendEmailDialog
            diagnosticId={id}
            defaultEmail={data?.diagnostic?.email || ""}
            company={data?.diagnostic?.company || ""}
          />
          <Button
            onClick={() => setConfirmDelete(true)}
            variant="outline"
            data-testid="delete-diagnostic-btn"
            title="Excluir diagnóstico"
            className="border-[#B9262B]/40 text-[#B9262B] hover:bg-[#B9262B]/10 hover:text-[#B9262B]"
          >
            <Trash2 size={14} className="mr-2" /> Excluir
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
            Diagnóstico
          </div>
          <h1 className="text-3xl font-bold tracking-tight">{d.company}</h1>
          <div className="text-sm text-[#9aa3ad] mt-1">
            CNPJ {d.cnpj || "—"} · Consultor {d.consultant || "—"} ·{" "}
            {d.created_at && new Date(d.created_at).toLocaleString("pt-BR")}
          </div>
        </div>
        <div className="p-4 rounded-xl border border-[#3A3F47] bg-[#1C1F26] text-right">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-1">
            Score Ponderado
          </div>
          <div className="text-4xl font-bold" style={{ color: statColors(total) }}>
            {total}%
          </div>
          <div className="text-xs text-[#E6E6E8] mt-0.5">{d.scoring?.level}</div>
        </div>
      </div>

      {/* Resumo executivo: Maturidade, Dimensão crítica, Economia */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <ExecCard
          label="Nível de Maturidade"
          value={d.scoring?.level_short || "—"}
          sub={d.scoring?.classification?.level}
          color={d.scoring?.classification?.color || "#6B8DB3"}
        />
        <ExecCard
          label="Dimensão mais crítica"
          value={d.scoring?.worst_section?.name || "—"}
          sub={
            d.scoring?.worst_section
              ? `Score ${Math.round(d.scoring.worst_section.score)}% · ${d.scoring.worst_section.level}`
              : "Sem dados suficientes"
          }
          color="#B9262B"
        />
        <ExecCard
          label="Potencial de Economia Mensal"
          value={
            d.scoring?.economy
              ? `${brl(d.scoring.economy.min_value)} — ${brl(d.scoring.economy.max_value)}`
              : "—"
          }
          sub={
            d.scoring?.economy
              ? `${d.scoring.economy.min_pct}% – ${d.scoring.economy.max_pct}% sobre custos controláveis (${brl(
                  d.scoring.economy.controllable_costs || 0
                )})`
              : ""
          }
          color="#22B673"
          small
        />
      </div>

      {/* Cadastrais */}
      <div className="p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26] mb-8">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-3">
          Dados cadastrais
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm">
          {[
            ["Responsável", d.fleet_manager],
            ["Telefone", d.phone],
            ["E-mail", d.email],
            ["Data da auditoria", d.audit_date],
            ["Veículos", d.vehicles_qty],
            ["Motoristas", d.drivers_qty],
            ["Tipo de frota", d.fleet_type],
            ["Tipo de operação", d.operation_type],
            ["Cidade/UF", d.city_uf],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-[#3A3F47]/50 py-1.5">
              <span className="text-[#6B8DB3]">{k}</span>
              <span className="text-white text-right">{v || "—"}</span>
            </div>
          ))}
        </div>
        {d.notes && (
          <div className="mt-4 pt-4 border-t border-[#3A3F47]">
            <div className="text-xs text-[#6B8DB3] mb-1">Observações</div>
            <div className="text-sm text-white whitespace-pre-wrap">{d.notes}</div>
          </div>
        )}
      </div>

      {/* Indicadores Financeiros Calculados */}
      {d.scoring?.kpis && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-5">
            <TrendingUp className="text-[#22B673]" />
            <h2 className="text-2xl font-semibold">Indicadores Financeiros Calculados</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <KpiCard label="Custo total mensal" value={brl(d.scoring.kpis.total_monthly_cost)} highlight />
            <KpiCard
              label="Custos controláveis"
              value={brl(d.scoring.kpis.controllable_costs)}
              sub="combustível + manutenção + pneus + multas + sinistros + perdas"
              highlight
            />
            <KpiCard
              label="Receita mensal"
              value={d.scoring.kpis.revenue != null ? brl(d.scoring.kpis.revenue) : "—"}
              sub="informada pela operação"
            />
            <KpiCard label="Custo por veículo" value={brl(d.scoring.kpis.cost_per_vehicle)} />
            <KpiCard label="Custo por km rodado" value={brl(d.scoring.kpis.cost_per_km)} />
            <KpiCard label="Combustível por km" value={brl(d.scoring.kpis.fuel_cost_per_km)} />
            <KpiCard label="Manutenção por veículo" value={brl(d.scoring.kpis.maintenance_per_vehicle)} />
            <KpiCard label="Pneus por veículo" value={brl(d.scoring.kpis.tires_per_vehicle)} />
            <KpiCard label="Multas por veículo" value={brl(d.scoring.kpis.fines_per_vehicle)} />
            <KpiCard label="Tecnologia por veículo" value={brl(d.scoring.kpis.tech_per_vehicle)} />
            <KpiCard
              label="Acidentes (12m) / veículo"
              value={num(d.scoring.kpis.accident_rate)}
              sub="quanto menor, melhor"
            />
            <KpiCard
              label="Roubos-furtos (12m) / veículo"
              value={num(d.scoring.kpis.theft_rate)}
              sub="exposição ao risco"
            />
            <KpiCard
              label="Perda por indisponibilidade"
              value={brl(d.scoring.kpis.stopped_loss)}
              sub="veículos parados × perda estimada"
            />
          </div>
        </div>
      )}

      {/* Plano de Ação Consolidado / Produto Recomendado */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-5">
          <Target className="text-[#B9262B]" />
          <h2 className="text-2xl font-semibold">Plano de Ação & Produto Recomendado</h2>
        </div>
        <p className="text-sm text-[#9aa3ad] mb-5">
          Recomendações geradas a partir do score de cada dimensão avaliativa.
          Use como base para a conversa consultiva com o cliente.
        </p>
        <div className="space-y-3">
          {sections.map((s) => {
            const sc = d.scoring?.by_section?.[s.id];
            if (!sc || sc.total_questions === 0 || !sc.classification) return null;
            const c = sc.classification;
            return (
              <div
                key={s.id}
                className="p-5 rounded-xl border border-[#3A3F47] bg-[#1C1F26]"
                style={{ borderLeft: `3px solid ${c.color}` }}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-white font-semibold text-base">{s.name}</div>
                    <div className="text-xs text-[#6B8DB3] mt-0.5">
                      Score{" "}
                      <span style={{ color: c.color }} className="font-mono font-semibold">
                        {Math.round(sc.score)}%
                      </span>{" "}
                      · Ponderado{" "}
                      <span className="text-white font-mono">
                        {sc.weighted_score?.toFixed(2)}
                      </span>{" "}
                      · Peso {sc.weight}%
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Chip label="Nível" value={c.level} color={c.color} />
                    <Chip label="Prioridade" value={c.priority} color={c.color} />
                    <Chip label="Prazo" value={c.timeline} color="#6B8DB3" />
                  </div>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[#0e1015] border border-[#3A3F47]">
                    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1">
                      <Package size={11} /> Produto recomendado
                    </div>
                    <div className="text-sm text-[#E6E6E8] leading-snug">
                      {c.product_recommendation}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-[#0e1015] border border-[#3A3F47]">
                    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1">
                      <CheckCircle2 size={11} /> Avaliação da dimensão
                    </div>
                    <div
                      className="text-sm leading-snug"
                      style={{
                        color:
                          c.dimension_evaluation === "Aceitável ou superior"
                            ? "#22B673"
                            : "#F5A623",
                      }}
                    >
                      {c.dimension_evaluation}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-6 flex items-center gap-3">
        <TrendingUp className="text-[#6B8DB3]" />
        <h2 className="text-2xl font-semibold">Respostas por Sessão</h2>
      </div>

      {/* Respostas por sessão */}
      <div className="space-y-6">
        {sections.map((s) => {
          const sc = d.scoring?.by_section?.[s.id];
          const sQs = questions.filter((q) => q.section_id === s.id);
          if (sQs.length === 0) return null;
          const isFinance = s.name === "Finanças";
          return (
            <div key={s.id} className="p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-white font-semibold text-lg">{s.name}</div>
                  <div className="text-xs text-[#6B8DB3]">
                    Peso da sessão: {s.weight}%{isFinance && " · não pontua no score"}
                  </div>
                </div>
                {!isFinance && sc && (
                  <div className="text-right">
                    <div className="font-mono font-bold text-2xl" style={{ color: statColors(sc.score || 0) }}>
                      {Math.round(sc.score || 0)}%
                    </div>
                    <div className="text-[10px] font-mono text-[#6B8DB3]">
                      {sc.points} / {sc.max_points} pts
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                {sQs.map((q) => {
                  if (isFinance) {
                    const val = valueMap[q.id];
                    return (
                      <div key={q.id} className="flex justify-between gap-4 py-2 border-b border-[#3A3F47]/40 last:border-0">
                        <div className="text-sm text-white flex-1">{q.text}</div>
                        <div className="font-mono text-sm text-white font-semibold">
                          {val != null
                            ? val.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                            : "—"}
                        </div>
                      </div>
                    );
                  }
                  const score = scoreMap[q.id];
                  const pts = score !== undefined ? score * q.weight : null;
                  const maxPts = q.weight * 4;
                  return (
                    <div key={q.id} className="flex justify-between gap-4 py-2 border-b border-[#3A3F47]/40 last:border-0">
                      <div className="text-sm text-white flex-1">{q.text}</div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-mono text-[#6B8DB3]">
                          {score !== undefined ? `${score}/4` : "—"}
                          {pts !== null && (
                            <span className="ml-2 text-[#22B673]">
                              {pts}/{maxPts} pts
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#E6E6E8]">
                          {score !== undefined ? SCALE_LABELS[score] : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Email */}
      <div className="mt-10 p-6 rounded-xl border border-[#3A3F47] bg-[#1C1F26]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-white font-semibold">Envio de e-mail</div>
            <div className="text-xs text-[#6B8DB3]">
              Use "Enviar por e-mail" no topo para anexar o Relatório e/ou a Proposta (PDF).
            </div>
          </div>
          <SendEmailDialog
            diagnosticId={id}
            defaultEmail={d.email || ""}
            company={d.company || ""}
          />
        </div>
        {d.email_sent_to?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#3A3F47]">
            <div className="text-xs text-[#6B8DB3] mb-2">Histórico de envios</div>
            <ul className="text-sm space-y-1">
              {d.email_sent_to.map((r, i) => (
                <li key={i} className="text-[#E6E6E8]">
                  → {r.to}
                  {r.attachments?.length > 0 && (
                    <span className="text-[#22B673] text-xs ml-2">
                      ({r.attachments.join(", ")})
                    </span>
                  )}{" "}
                  <span className="text-[#6B8DB3] text-xs">
                    em {new Date(r.sent_at).toLocaleString("pt-BR")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={(v) => !deleting && setConfirmDelete(v)}>
        <AlertDialogContent
          data-testid="delete-diagnostic-dialog"
          className="bg-[#1C1F26] border-[#3A3F47] text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir diagnóstico?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9aa3ad]">
              Esta ação é permanente. Todos os dados, respostas e propostas vinculadas a{" "}
              <strong className="text-white">"{d.company}"</strong> serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              className="bg-[#0e1015] border-[#3A3F47] text-white hover:bg-[#252932] hover:text-white"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-diagnostic-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              {deleting ? <Loader2 className="animate-spin" size={16} /> : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Chip = ({ label, value, color }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs"
       style={{ borderColor: color, background: `${color}1A` }}>
    <span className="text-[#9aa3ad] uppercase tracking-wider text-[9px]">{label}</span>
    <span className="font-semibold text-white">{value}</span>
  </div>
);

const ExecCard = ({ label, value, sub, color, small }) => (
  <div className="p-5 rounded-xl border border-[#3A3F47] bg-[#1C1F26]" style={{ borderTop: `2px solid ${color}` }}>
    <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-2">
      {label}
    </div>
    <div
      className={small ? "text-base font-semibold leading-tight" : "text-2xl font-bold leading-tight"}
      style={{ color }}
    >
      {value}
    </div>
    {sub && <div className="text-xs text-[#9aa3ad] mt-1 leading-snug">{sub}</div>}
  </div>
);

const KpiCard = ({ label, value, sub, highlight }) => (
  <div
    className={`p-4 rounded-lg border ${
      highlight ? "border-[#22B673]/40 bg-[#22B673]/5" : "border-[#3A3F47] bg-[#1C1F26]"
    }`}
  >
    <div className="text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1">
      {label}
    </div>
    <div
      className={`font-mono font-semibold ${highlight ? "text-[#22B673] text-lg" : "text-white text-base"}`}
    >
      {value}
    </div>
    {sub && <div className="text-[10px] text-[#6B8DB3] mt-1">{sub}</div>}
  </div>
);

const brl = (v) => {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const num = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
};

export default AdminDiagnosticDetail;
